import {useState} from 'react'
import {MfaCodeInput} from './MfaCodeInput'
import {MfaMethodPicker} from './MfaMethodPicker'
import {
  MfaMethodType,
  respondMfaChallenge,
  resendMfaChallenge,
  selectMfaChallengeMethod,
  startMfaEnrollment,
  verifyMfaEnrollment,
} from '../core/mfaApi'
import {AuthModel} from '../core/_models'

type Step = 'setup' | 'picker' | 'verify' | 'enroll-contact' | 'enroll-totp' | 'enroll-verify'

type Props = {
  mfaSession: string
  requiresSetup: boolean
  availableMethods?: string[]
  onComplete: (auth: AuthModel) => void
  onCancel: () => void
}

const mapAvailableMethods = (availableMethods?: string[]): MfaMethodType[] =>
  (availableMethods ?? []).map((m) => {
    if (m.toLowerCase() === 'sms') return 0 as MfaMethodType
    if (m.toLowerCase() === 'email') return 1 as MfaMethodType
    return 2 as MfaMethodType
  })

export function MfaLoginFlow({mfaSession, requiresSetup, availableMethods, onComplete, onCancel}: Props) {
  const methods: MfaMethodType[] = requiresSetup ? [2, 0, 1] : mapAvailableMethods(availableMethods)

  const [step, setStep] = useState<Step>(requiresSetup ? 'setup' : availableMethods && availableMethods.length > 1 ? 'picker' : 'verify')
  const [session, setSession] = useState(mfaSession)
  const [selectedMethod, setSelectedMethod] = useState<MfaMethodType | null>(methods[0] ?? null)
  const [contact, setContact] = useState('')
  const [code, setCode] = useState('')
  const [qrUri, setQrUri] = useState('')
  const [secretKey, setSecretKey] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const finishAuth = (data: any) => {
    onComplete({
      isAuthenticated: true,
      message: '',
      userName: data.userName,
      email: data.email,
      roles: data.roles ?? [],
      token: data.token,
      refreshTokenExpiration: data.refreshTokenExpiration,
    })
  }

  const startEnroll = async (method: MfaMethodType) => {
    setSelectedMethod(method)
    if (method === 2) {
      setLoading(true)
      setError('')
      try {
        const {data} = await startMfaEnrollment({method, mfaSession: session})
        setQrUri(data.qrCodeUri ?? '')
        setSecretKey(data.secretKey ?? '')
        setSession(data.mfaSession ?? session)
        setStep('enroll-totp')
      } catch {
        setError('Failed to start setup')
      } finally {
        setLoading(false)
      }
    } else {
      setStep('enroll-contact')
    }
  }

  const sendEnrollContact = async () => {
    if (selectedMethod == null || !contact.trim()) return
    setLoading(true)
    setError('')
    try {
      const {data} = await startMfaEnrollment({method: selectedMethod, contactValue: contact, mfaSession: session})
      setSession(data.mfaSession ?? session)
      setStep('enroll-verify')
    } catch {
      setError('Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }

  const verifyEnroll = async () => {
    if (selectedMethod == null || code.length < 6) return
    setLoading(true)
    setError('')
    try {
      const {data} = await verifyMfaEnrollment({method: selectedMethod, code, mfaSession: session})
      if (!data.success) {
        setError('Invalid code')
        return
      }
      finishAuth(data)
    } catch {
      setError('Verification failed')
    } finally {
      setLoading(false)
    }
  }

  const selectMethod = async (method: MfaMethodType) => {
    setSelectedMethod(method)
    setLoading(true)
    setError('')
    try {
      const {data} = await selectMfaChallengeMethod(method, session)
      setSession(data.mfaSession ?? session)
      setStep('verify')
    } catch {
      setError('Failed to select method')
    } finally {
      setLoading(false)
    }
  }

  const verifyChallenge = async () => {
    if (code.length < 6) return
    setLoading(true)
    setError('')
    try {
      const {data} = await respondMfaChallenge(code, session)
      if (!data.success) {
        setError(data.message ?? 'Invalid code')
        return
      }
      finishAuth(data)
    } catch {
      setError('Verification failed')
    } finally {
      setLoading(false)
    }
  }

  const resend = async () => {
    setLoading(true)
    try {
      const {data} = await resendMfaChallenge(session)
      setSession(data.mfaSession ?? session)
    } finally {
      setLoading(false)
    }
  }

  const handleMethodContinue = async () => {
    if (selectedMethod == null) return
    if (step === 'setup') {
      await startEnroll(selectedMethod)
      return
    }
    if (step === 'picker') {
      await selectMethod(selectedMethod)
    }
  }

  const showMethodPicker = step === 'setup' || step === 'picker'

  return (
    <div className='w-100'>
      {error && <div className='alert alert-danger mb-5'>{error}</div>}

      {showMethodPicker && (
        <MfaMethodPicker
          methods={methods}
          selectedMethod={selectedMethod}
          onSelect={setSelectedMethod}
          onContinue={handleMethodContinue}
          loading={loading}
          title='Choose a verification method'
          subtitle={
            step === 'setup'
              ? "Select how you'd like to receive your verification codes when signing in"
              : 'Select how you would like to verify your identity'
          }
        />
      )}

      {!showMethodPicker && (
        <div className='mb-8'>
          <h2 className='text-dark fw-bold fs-2 mb-2'>
            {requiresSetup ? 'Set up Multi-Factor Authentication' : 'Enter Verification Code'}
          </h2>
          <p className='text-gray-600'>
            {requiresSetup
              ? 'Complete setup for your selected verification method.'
              : 'Enter the code from your selected verification method.'}
          </p>
        </div>
      )}

      {step === 'enroll-contact' && (
        <>
          <input
            className='form-control mb-4'
            placeholder={selectedMethod === 1 ? 'Email address' : 'Phone number'}
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
          <button
            type='button'
            className='btn w-100'
            style={{backgroundColor: '#d7383c', color: '#fff'}}
            onClick={sendEnrollContact}
            disabled={loading}
          >
            Send Verification Code
          </button>
        </>
      )}

      {step === 'enroll-totp' && (
        <>
          {qrUri && (
            <div className='text-center mb-4'>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUri)}`}
                alt='QR'
              />
              <p className='text-muted mt-3 small'>Can&apos;t scan? Enter this key manually: {secretKey}</p>
            </div>
          )}
          <button
            type='button'
            className='btn w-100 mb-4'
            style={{backgroundColor: '#d7383c', color: '#fff'}}
            onClick={() => setStep('enroll-verify')}
          >
            I&apos;ve Scanned the Code
          </button>
        </>
      )}

      {(step === 'enroll-verify' || step === 'verify') && (
        <>
          <MfaCodeInput value={code} onChange={setCode} />
          <button
            type='button'
            className='btn w-100 mt-6'
            style={{backgroundColor: '#d7383c', color: '#fff'}}
            onClick={step === 'enroll-verify' ? verifyEnroll : verifyChallenge}
            disabled={loading || code.length < 6}
          >
            Confirm & Finish Setup
          </button>
          {step === 'verify' && (
            <div className='d-flex flex-column gap-2 mt-4 text-center'>
              <button type='button' className='btn btn-link' onClick={resend} disabled={loading}>
                Didn&apos;t get a code? Send OTP again
              </button>
              {methods.length > 1 && (
                <button
                  type='button'
                  className='btn btn-link'
                  onClick={() => {
                    setError('')
                    setCode('')
                    setStep('picker')
                  }}
                >
                  Use a different method
                </button>
              )}
            </div>
          )}
        </>
      )}

      <button type='button' className='btn btn-link mt-6' onClick={onCancel}>
        Back to sign in
      </button>
    </div>
  )
}
